import {useRouter} from 'next/router';
import {useCallback, useState} from 'react';
import {tt} from '../../../../../../common/getTranslations';
import {requestContext} from '../../../../../../common/requestContext';
import {twMerge} from '../../../../../../common/twMerge';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {NonstopWatchingDialog} from '../../../../../../design-system/organisms/NonstopWatchingDialog/NonstopWatchingDialog';
import {ViewerNavbarMenu} from '../../../../../../design-system/organisms/ViewerNavbarMenu/ViewerNavbarMenu';
import {SaleMethod} from '../../../../../../graphql/generated/types';
import {putTiaraObjectProps} from '../../../../../../logging/putTiaraObjectProps';
import {ONLY_DATA_CONFIG} from '../../../../../../reactQuery/constant';
import {showToast} from '../../../../../../toast/toastHandler';
import {useContentHomeOverviewQuery} from '../../../../../../wrapper/contentWrapper';
import {useViewerInfoQuery} from '../../../../../../wrapper/viewerWrapper';
import {VIEWER_TOAST_MESSAGES} from '../constant/viewerConstant';
import {useViewerMenuHandler} from '../hook/useViewerMenuHandler';
import {setNonstopWatching} from '../state/viewerInfoSlice';
export interface MobileViewerNavMenuContainerProps {
  seriesId: number;
  productId: number;
}

export function MobileViewerNavMenuContainer({
  seriesId,
  productId,
}: MobileViewerNavMenuContainerProps) {
  const router = useRouter();
  const dispatch = useTypedDispatch();
  const {isMobile, isWebView} = requestContext.requestInfo.display;

  const {handleMoveNextProduct, handleMovePrevProduct} = useViewerMenuHandler({
    isMobile,
  });

  const isCashTypeViewer =
    useContentHomeOverviewQuery({seriesId}, ONLY_DATA_CONFIG).data
      ?.contentHomeOverview?.content?.saleMethod === SaleMethod.Cash;
  const {isViewerMenuExposed, isNonstopWatching} = useTypedSelector(state => ({
    isViewerMenuExposed: state.viewerMenu.isExposed,
    isNonstopWatching:
      !!state.viewerInfo.nonstopWatchingInSeries[seriesId]?.isNonstopWatching,
  }));

  const viewerData = useViewerInfoQuery(
    {seriesId, productId},
    ONLY_DATA_CONFIG
  ).data;
  const commentCount = viewerData?.item.serviceProperty?.commentCount;

  const [isOpenNonstopWatchingDialog, setIsOpenNonstopWatchingDialog] =
    useState(false);

  const handleNonstopWatchingOn = useCallback(() => {
    dispatch(setNonstopWatching({seriesId, isNonstopWatching: true}));
    setIsOpenNonstopWatchingDialog(false);
    showToast({
      message: tt(VIEWER_TOAST_MESSAGES.nonStopWatchingOn),
      isMobile: requestContext.requestInfo.display.isMobile,
    });
  }, [dispatch, seriesId]);

  const handleNonstopWatchingOff = useCallback(() => {
    dispatch(setNonstopWatching({seriesId, isNonstopWatching: false}));
    showToast({
      message: tt(VIEWER_TOAST_MESSAGES.nonStopWatchingOff),
      isMobile: requestContext.requestInfo.display.isMobile,
    });
  }, [dispatch, seriesId]);

  const onClickNonStopWatchingButton = useCallback(() => {
    // 이미 런모드가 활성화 되어있으면 Off, 아닐경우 팝업을 띄워 정주행을 진행할지 물어봅니다
    if (!isNonstopWatching) {
      setIsOpenNonstopWatchingDialog(true);
    } else {
      handleNonstopWatchingOff();
    }
  }, [handleNonstopWatchingOff, isNonstopWatching]);

  const onClickCommentButton = useCallback(() => {
    router.push(`/content/${seriesId}/viewer/${productId}/comment`);
  }, [productId, router, seriesId]);

  if (isWebView || !isMobile || !viewerData) {
    return null;
  }

  return (
    <>
      <div
        className={twMerge(
          'fixed bottom-0 z-150 w-full items-center bg-theme-bg-base pb-safe',
          !isViewerMenuExposed && 'hidden'
        )}
        {...putTiaraObjectProps({
          click: {
            layer1: '뷰어메뉴',
            layer2: '하단',
          },
        })}
      >
        <ViewerNavbarMenu
          isMobile={isMobile}
          commentCount={commentCount ? commentCount : undefined}
          isPrevButtonEnabled={!!viewerData.prevItem}
          isNextButtonEnabled={!!viewerData.nextItem}
          isNonstopWatching={isNonstopWatching}
          onClickNonStopWatchingButton={onClickNonStopWatchingButton}
          onClickCommentButton={onClickCommentButton}
          onClickPrevButton={handleMovePrevProduct}
          onClickNextButton={handleMoveNextProduct}
          prevButtonTiaraObject={
            viewerData.prevItem &&
            putTiaraObjectProps({
              eventMeta: {
                id: String(viewerData.prevItem.productId),
                type: 'productId',
              },
            })
          }
          nextButtonTiaraObject={
            viewerData.nextItem &&
            putTiaraObjectProps({
              eventMeta: {
                id: String(viewerData.nextItem.productId),
                type: 'productId',
              },
            })
          }
          className="bg-theme-bg-base before:absolute before:left-0 before:top-0 before:z-[-1] before:h-57pxr before:w-full" // 특정 iOS 사파리 브라우저에서 틈새가 발생하는 이슈를 위한 속성입니다. 1px 큰 요소를 만들어 배경으로 씁니다
        />
      </div>
      {/* 정주행 안내 Dialog */}
      {isOpenNonstopWatchingDialog && (
        <NonstopWatchingDialog
          isOpen={isOpenNonstopWatchingDialog}
          onClose={() => setIsOpenNonstopWatchingDialog(false)}
          onClickConfirm={handleNonstopWatchingOn}
          isMobile={isMobile}
          isNonstopWatching={isNonstopWatching}
          isCashTypeViewer={isCashTypeViewer}
        />
      )}
    </>
  );
}
